import { call, put, select } from "redux-saga/effects";

import { AlertStatusMessage, IApiError, IOrder, OrderStatus, ToggleActionType } from '@/shared/types';

import { IGetManyOrdersParams } from "../model/types/interfaces";
import { selectParams } from "../model/selectors";
import {
  modifyOrdersData,
} from "./helpers";
import {
  sendManageOrderRequest,
  sendGetOrderRequest,
  sendDeleteClientOrderRequest,
  getManyOrders,
  sendAssignOrderRequest,
  sendConfirmOrderRequest,
  sendCreateOrderShipmentRequest,
} from './senders';
import { commonSlice } from "@/entities/common";
import { ordersSlice } from "../model/slice";
import { asideSagaActions, asideSlice } from '@/widgets/side-block';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  AssignOrderPayload,
  GetOrderPayload,
  GetOrderResponse,
  UpdateOrderPayload,
} from '@/entities/client-orders/api/types/types';
import { getFullAddress } from '@/entities/client-companies/api/helpers';
import { t } from 'i18next';
import { alertsSagaActions } from '@/entities/alerts';
import { dialogSagaActions, dialogSlice } from '@/widgets/dialog';
import { ordersSagaActions } from '@/entities/client-orders';

export function* handleRetrieveManyOrders() {
  const { setOrders } = ordersSlice;
  const { setLoading } = commonSlice;
  const body: IGetManyOrdersParams = yield select(selectParams);

  yield put(setLoading({ isLoading: true }));

  const { items, count } = yield call(getManyOrders, body);
  const modifiedOrders: Array<IOrder> = yield call(
    modifyOrdersData,
    items
  );

  yield put(setOrders({ orders: { items: modifiedOrders, count } }));
  yield put(setLoading({ isLoading: false }));
}

export function* handleRetrieveOrder({ payload }: PayloadAction<GetOrderPayload>) {
  const { orderId } = payload;
  const { setActiveEntity } = commonSlice;
  const { setLoadingAside } = asideSlice;

  const response: GetOrderResponse = yield call(sendGetOrderRequest, orderId);
  const order: IOrder = response.data.data;
;
  const fullAddress = getFullAddress(order?.shippingLocation?.address1, order?.shippingLocation?.address2, order?.shippingLocation?.city, order?.shippingLocation?.state, order?.shippingLocation?.zip) || ''

  const modifiedOrder = {
    ...order,
    fullAddress,
  };

  yield put(setActiveEntity({ activeEntity: modifiedOrder }));
  yield put(setLoadingAside({ isLoading: false }));
}

export function* handleDeleteOrder({ payload }: PayloadAction<GetOrderPayload>) {
  const { orderId } = payload;
  const { setLoadingAside } = asideSlice;
  const { toggleDialog } = dialogSagaActions;
  const { toggleSideBlock } = asideSagaActions;

  yield call(sendDeleteClientOrderRequest, orderId);

  yield put(setLoadingAside({ isLoading: false }));
  yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
  yield put(toggleSideBlock({ status: ToggleActionType.CLOSE }));
  yield call(handleRetrieveManyOrders);
}

export function* handleAssignOrder({ payload }: PayloadAction<AssignOrderPayload>) {
  const { orderId } = payload;
  const { setActiveEntity } = commonSlice;
  const { setLoadingAside } = asideSlice;
  const { updateAlert } = alertsSagaActions;

  const response: GetOrderResponse = yield call(sendAssignOrderRequest, orderId);
  const order: IOrder = response.data.data;

  const fullAddress = getFullAddress(order?.shippingLocation?.address1, order?.shippingLocation?.address2, order?.shippingLocation?.city, order?.shippingLocation?.state, order?.shippingLocation?.zip) || ''

  const modifiedOrder = {
    ...order,
    fullAddress,
  };

  yield put(setActiveEntity({ activeEntity: modifiedOrder }));
  yield put(setLoadingAside({ isLoading: false }));
  yield put(
    updateAlert({
      status: AlertStatusMessage.SUCCESS,
      message: `${t("order_assigned_successfully")}`,
    })
  );
  yield call(handleRetrieveManyOrders);
}

export function* handlePaidOrder({ payload }: PayloadAction<AssignOrderPayload>) {
  const { orderId } = payload;
  const { setActiveEntity } = commonSlice;
  const { setLoadingAside } = asideSlice;
  const { setLoadingDialog } = dialogSlice;
  const { toggleDialog } = dialogSagaActions;
  const { updateAlert } = alertsSagaActions;

  try {
    const response: GetOrderResponse = yield call(sendConfirmOrderRequest, orderId, {status: 'paid'});
    const order: IOrder = response.data.data;

    const fullAddress = getFullAddress(order?.shippingLocation?.address1, order?.shippingLocation?.address2, order?.shippingLocation?.city, order?.shippingLocation?.state, order?.shippingLocation?.zip) || ''
    const modifiedOrder = { ...order, fullAddress };

    yield put(setActiveEntity({ activeEntity: modifiedOrder }));
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("order_paid_successfully")}`,
      })
    );
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield call(handleRetrieveManyOrders);
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingAside({ isLoading: false }));
    yield put(setLoadingDialog({ isLoading: false }));
  }
}

export function* handleCompleteOrder({ payload }: PayloadAction<AssignOrderPayload>) {
  const { orderId } = payload;
  const { setActiveEntity } = commonSlice;
  const { setLoadingAside } = asideSlice;
  const { setLoadingDialog } = dialogSlice;
  const { toggleDialog } = dialogSagaActions;
  const { updateAlert } = alertsSagaActions;

  try {
    const response: GetOrderResponse = yield call(sendConfirmOrderRequest, orderId, {status: OrderStatus.SHIPPED, completionDate: new Date().toISOString()});
    const order: IOrder = response.data.data;

    const fullAddress = getFullAddress(order?.shippingLocation?.address1, order?.shippingLocation?.address2, order?.shippingLocation?.city, order?.shippingLocation?.state, order?.shippingLocation?.zip) || ''
    const modifiedOrder = { ...order, fullAddress };

    yield put(setActiveEntity({ activeEntity: modifiedOrder }));
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("order_completed_successfully")}`,
      })
    );
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield call(handleRetrieveManyOrders);
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingAside({ isLoading: false }));
    yield put(setLoadingDialog({ isLoading: false }));
  }
}

export function* handleManageOrder({ payload }: PayloadAction<UpdateOrderPayload>) {
  const { orderId, formData } = payload;
  const { setLoadingAside } = asideSlice;
  const { setActiveEntity } = commonSlice;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { setLoadingDialog } = dialogSlice;

  try {
    const response: GetOrderResponse = yield call(sendManageOrderRequest, orderId, formData);
    const order: IOrder = response.data.data;

    const fullAddress = getFullAddress(order?.shippingLocation?.address1, order?.shippingLocation?.address2, order?.shippingLocation?.city, order?.shippingLocation?.state, order?.shippingLocation?.zip) || ''
    const modifiedOrder = { ...order, fullAddress };

    yield put(setActiveEntity({ activeEntity: modifiedOrder }));
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t(payload.status === OrderStatus.CANCELLED ? "order_rejected_successfully" : "order_updated_successfully")}`,
      })
    );
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield call(handleRetrieveManyOrders);
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingAside({ isLoading: false }));
    yield put(setLoadingDialog({ isLoading: false }));
  }
}

export function* handleManageShipmentOrder({ payload }: PayloadAction<UpdateOrderPayload>) {
  const { orderId, formData } = payload;
  const { setLoadingAside } = asideSlice;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { setLoadingDialog } = dialogSlice;
  const { retrieveOrder, completeOrder } = ordersSagaActions;

  // if (status === OrderStatus.CONFIRMED) {
  //   yield call(sendManageOrderRequest, orderId, { status: OrderStatus.DISPATCHED })
  // }
  try {
    yield call(sendCreateOrderShipmentRequest, orderId, formData);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("shipping_added_successfully")}`,
      })
    );
    yield put(completeOrder({ orderId }));
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield put(retrieveOrder({ orderId }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingAside({ isLoading: false }));
    yield put(setLoadingDialog({ isLoading: false }));
  }
}

function* handleError(err: any) {
  const { updateAlert } = alertsSagaActions;
  const error = err as IApiError;
  const backendError = error?.response?.data?.error;
  const rawMessage = backendError?.message;
  const messages = Array.isArray(rawMessage)
    ? rawMessage.filter(
        (message): message is string =>
          typeof message === "string" && Boolean(message)
      )
    : typeof rawMessage === "string" && rawMessage
      ? [rawMessage]
      : [];

  if (!messages.length) {
    messages.push(backendError?.title || t("order_update_failed"));
  }

  for (const message of messages) {
    yield put(updateAlert({ status: AlertStatusMessage.ERROR, message }));
  }
}
